import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, Save, RotateCcw, X } from 'lucide-react';
import {
  MessageType,
  MESSAGE_TYPE_LABELS,
  MESSAGE_TYPE_DESCRIPTIONS,
} from '@/types/system-message-templates';
import { SystemVariableDropdown } from './SystemVariableDropdown';
import { SystemMessagePreview } from './SystemMessagePreview';
import { useSystemMessageTemplates } from '@/hooks/useSystemMessageTemplates';
import { useUpdateSystemMessageTemplate } from '@/hooks/useUpdateSystemMessageTemplate';
import { useResetSystemMessageTemplate } from '@/hooks/useResetSystemMessageTemplate';

interface SystemTemplateCardProps {
  messageType: MessageType;
}

const MAX_CHARS = 4096;

export function SystemTemplateCard({ messageType }: SystemTemplateCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: templates } = useSystemMessageTemplates();
  const updateMutation = useUpdateSystemMessageTemplate();
  const resetMutation = useResetSystemMessageTemplate();

  const template = templates?.find((t) => t.message_type === messageType);
  const originalContent = template?.template_content || '';

  // Initialize edited content when template loads
  useEffect(() => {
    if (template && !editedContent) {
      setEditedContent(template.template_content);
    }
  }, [template, editedContent]);

  const hasChanges = editedContent !== originalContent;
  const charCount = editedContent.length;
  const isOverLimit = charCount > MAX_CHARS;
  const isNearLimit = charCount > MAX_CHARS * 0.95;

  const handleVariableClick = (variableName: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const cursorPos = textarea.selectionStart;
    const textBefore = editedContent.substring(0, cursorPos);
    const textAfter = editedContent.substring(cursorPos);
    const variableText = `{{${variableName}}}`;

    const newContent = textBefore + variableText + textAfter;
    setEditedContent(newContent);

    // Set cursor position after the inserted variable
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = cursorPos + variableText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleSave = () => {
    if (isOverLimit) return;
    updateMutation.mutate({
      messageType,
      template_content: editedContent,
    });
  };

  const handleReset = () => {
    resetMutation.mutate(messageType, {
      onSuccess: (data) => {
        setEditedContent(data.template_content);
        setShowResetModal(false);
      },
    });
  };

  const handleCancel = () => {
    setEditedContent(originalContent);
    setIsExpanded(false);
  };

  const handleExpand = () => {
    setIsExpanded(true);
    // Focus textarea after expansion
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
  };

  if (!template) {
    return null;
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div
          className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => !isExpanded && handleExpand()}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-base font-semibold text-[#212121] mb-1">
                {MESSAGE_TYPE_LABELS[messageType]}
              </h3>
              <p className="text-sm text-gray-600">
                {MESSAGE_TYPE_DESCRIPTIONS[messageType]}
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="ml-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {isExpanded ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 p-4">
            {/* Variable Dropdown */}
            <div className="mb-4">
              <SystemVariableDropdown
                messageType={messageType}
                onInsert={handleVariableClick}
              />
            </div>

            {/* Editor and Preview Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left: Textarea */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Template Content
                </label>
                <textarea
                  ref={textareaRef}
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="w-full h-96 p-3 border border-gray-300 rounded-lg focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all resize-none font-mono text-sm"
                  placeholder="Enter your template content..."
                />

                {/* Character Counter */}
                <div className="flex items-center justify-between">
                  <div
                    className={`text-sm ${
                      isOverLimit
                        ? 'text-red-600 font-medium'
                        : isNearLimit
                        ? 'text-orange-500'
                        : 'text-gray-500'
                    }`}
                  >
                    {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()} characters
                    {isOverLimit && ' - Template is too long'}
                  </div>
                </div>
              </div>

              {/* Right: Preview */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Preview
                </label>
                <div className="h-96">
                  <SystemMessagePreview
                    messageType={messageType}
                    templateContent={editedContent}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>

              <button
                onClick={() => setShowResetModal(true)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                disabled={resetMutation.isPending}
              >
                <RotateCcw className="w-4 h-4" />
                Reset to Default
              </button>

              <button
                onClick={handleSave}
                disabled={!hasChanges || isOverLimit || updateMutation.isPending}
                className="px-6 py-2 bg-[#5B8E87] text-white rounded-lg hover:bg-[#4F7A74] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-[#212121] mb-2">
              Reset to Default?
            </h3>
            <p className="text-gray-600 mb-4">
              This will restore the default template for {MESSAGE_TYPE_LABELS[messageType]}. Your current changes will be lost.
            </p>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowResetModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={resetMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={resetMutation.isPending}
              >
                {resetMutation.isPending ? 'Resetting...' : 'Reset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
